import { useMemo, useState } from 'react'
import { ChartLineUp, Plus, StackPlus } from '@phosphor-icons/react'
import { useFormat } from '@/i18n'
import { istDateKey } from '@/lib/ist'
import { useCan } from '@/auth/context'
import { Badge, Banner, DataTable, EmptyState, Field, Input, Select, type Column } from '@/ui'
import { errorMessage } from '../api/core'
import { useCrops } from '../api/reference'
import {
  useLoadYieldBand,
  useMachines,
  useSteps,
  useVarieties,
  useYieldBands,
  type StepOption,
  type YieldBandRow,
} from '../api/processing'
import { ActionButton } from '../components/Can'
import { FilterBar, SelectFilter } from '../components/FilterBar'
import { FormDialog } from '../components/FormDialog'
import { ConsolePage } from '../components/Layout'
import { Code } from '../components/bits'
import { useUrlState } from '../lib/useUrlState'
import { serverProblem } from '../model/serverError'
import {
  BANDLESS_STEPS,
  draftFromBand,
  emptyYieldBandDraft,
  previousBand,
  trimDecimal,
  validateYieldBandDraft,
  yieldBandBody,
  yieldBandOk,
  type YieldBandDraft,
} from '../model/yieldBandForm'

const LIMIT = 100

const pctText = (v: string | null) => (v === null ? null : `${trimDecimal(v)} %`)
const kgText = (v: string | null) => (v === null ? null : `${trimDecimal(v)} kg`)

type Editing = { mode: 'new'; preset: Partial<YieldBandDraft> } | { mode: 'version'; row: YieldBandRow }

/**
 * Yield bands (machine settings rows, F6-B1.c): the yield range a run of a crop on a step (and
 * machine) must land in. A run with no band, or outside it, closes BALANCE_OUT_OF_BAND and goes to
 * the exception queue. The run uses the newest version in force on its day — a variety's band
 * before the crop's, this machine's before the all-machines band. Bands are never edited: a
 * change is a new version with a note.
 */
export function YieldBandsPage() {
  const [f, setF, { reset, active }] = useUrlState({ cropId: '', stepCode: '', equipmentId: '', versions: '', page: 1 })
  const q = useYieldBands({
    page: f.page,
    limit: LIMIT,
    cropId: f.cropId || undefined,
    stepCode: f.stepCode || undefined,
    equipmentId: f.equipmentId || undefined,
    latest: f.versions !== 'all',
  })
  const crops = useCrops()
  const steps = useSteps()
  const machines = useMachines()
  const can = useCan()
  const fmt = useFormat()
  const [editing, setEditing] = useState<Editing | null>(null)
  const rows = q.data?.rows ?? []
  const today = istDateKey()
  const stepOf = useMemo(() => new Map((steps.data ?? []).map((s) => [s.code, s])), [steps.data])

  const columns: Column<YieldBandRow>[] = [
    {
      id: 'crop',
      header: 'Crop · variety',
      cell: (r) => (
        <span className="flex flex-col">
          <span className="font-semibold">{r.cropName ?? '—'}</span>
          <span className="text-[13px] text-muted">{r.varietyName ?? 'Every variety'}</span>
        </span>
      ),
    },
    { id: 'step', header: 'Step', cell: (r) => stepOf.get(r.stepCode)?.label ?? <Code>{r.stepCode}</Code> },
    {
      id: 'machine',
      header: 'Machine',
      cell: (r) => (r.equipmentId ? <Code>{r.equipmentCode ?? r.equipmentId.slice(0, 8)}</Code> : <span className="text-muted">All machines</span>),
    },
    {
      id: 'version',
      header: 'Version · from',
      cell: (r) => (
        <span className="inline-flex flex-wrap items-center gap-1.5">
          <span className="font-mono">v{r.version}</span>
          <span className="whitespace-nowrap">{fmt.date(r.effectiveFrom)}</span>
          {r.effectiveFrom > today && <Badge tone="warn">Not yet in force</Badge>}
        </span>
      ),
      hideBelow: 'sm',
    },
    {
      id: 'range',
      header: 'Yield range',
      align: 'right',
      mono: true,
      cell: (r) => `${trimDecimal(r.minPct)}–${trimDecimal(r.maxPct)} %`,
    },
    { id: 'flush', header: 'Min flush', align: 'right', mono: true, cell: (r) => kgText(r.minFlushKg) ?? '—', hideBelow: 'md' },
    { id: 'input', header: 'Min input', align: 'right', mono: true, cell: (r) => kgText(r.minInputKg) ?? '—', hideBelow: 'md' },
    {
      id: 'loss',
      header: 'Loss limit',
      align: 'right',
      mono: true,
      cell: (r) =>
        pctText(r.maxUnaccountedPct) ?? (
          <span className="text-muted" title="The step's loss limit (no limit on the band)">
            {pctText(stepOf.get(r.stepCode)?.lossLimitPct ?? null) ?? '—'} · step
          </span>
        ),
      hideBelow: 'lg',
    },
    {
      id: 'actions',
      header: <span className="sr-only">Actions</span>,
      align: 'right',
      cell: (r) => (
        <span className="flex justify-end" onClick={(e) => e.stopPropagation()}>
          <ActionButton perm="yield-bands:create" hide size="sm" variant="ghost" leadingIcon={<StackPlus />} onClick={() => setEditing({ mode: 'version', row: r })}>
            New version
          </ActionButton>
        </span>
      ),
    },
  ]

  return (
    <ConsolePage
      eyebrow="Processing"
      title="Yield bands"
      description="The yield range each run must land in, per crop and step — for one machine or all machines. A run with no band, or outside its band, does not balance and goes to Exceptions. Bands are never edited: a change is a new version from a date, with a note."
      actions={
        <ActionButton
          perm="yield-bands:create"
          leadingIcon={<Plus />}
          onClick={() => setEditing({ mode: 'new', preset: { cropId: f.cropId, stepCode: f.stepCode, equipmentId: f.equipmentId } })}
        >
          New band
        </ActionButton>
      }
    >
      <DataTable<YieldBandRow>
        caption="Yield bands"
        columns={columns}
        rows={rows}
        getRowId={(r) => r.id}
        loading={q.isLoading}
        error={q.isError ? errorMessage(q.error) : undefined}
        onRowClick={can('yield-bands:create') ? (r) => setEditing({ mode: 'version', row: r }) : undefined}
        pageSize={LIMIT}
        server={{ page: f.page, total: q.data?.total ?? 0, onPageChange: (page) => setF({ page }) }}
        empty={
          <EmptyState
            icon={<ChartLineUp />}
            title={active ? 'No bands match' : 'No yield bands yet'}
            description="Add an all-machines band for each crop and step you run; add a machine's own band where it yields differently."
            compact
          />
        }
        filters={
          <FilterBar onClear={reset} active={active}>
            <SelectFilter
              label="Crop"
              value={f.cropId}
              onChange={(cropId) => setF({ cropId })}
              options={(crops.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
            />
            <SelectFilter
              label="Step"
              value={f.stepCode}
              onChange={(stepCode) => setF({ stepCode })}
              options={(steps.data ?? []).filter((s) => !BANDLESS_STEPS.has(s.code)).map((s) => ({ value: s.code, label: s.label }))}
            />
            <SelectFilter
              label="Machine"
              value={f.equipmentId}
              onChange={(equipmentId) => setF({ equipmentId })}
              options={(machines.data ?? []).map((m) => ({ value: m.id, label: `${m.code} · ${m.name}` }))}
            />
            <SelectFilter<'all'>
              label="Versions"
              value={f.versions === 'all' ? 'all' : ''}
              onChange={(versions) => setF({ versions })}
              allLabel="Newest only"
              options={[{ value: 'all', label: 'Every version' }]}
            />
          </FilterBar>
        }
      />
      {editing && (
        <YieldBandDialog
          editing={editing}
          crops={crops.data ?? []}
          steps={(steps.data ?? []).filter((s) => !BANDLESS_STEPS.has(s.code))}
          onClose={() => setEditing(null)}
        />
      )}
    </ConsolePage>
  )
}

function YieldBandDialog({
  editing,
  crops,
  steps,
  onClose,
}: {
  editing: Editing
  crops: ReadonlyArray<{ id: string; name: string }>
  steps: readonly StepOption[]
  onClose: () => void
}) {
  const from = editing.mode === 'version' ? editing.row : null
  const [d, setD] = useState<YieldBandDraft>(from ? draftFromBand(from) : { ...emptyYieldBandDraft(), ...(editing.mode === 'new' ? editing.preset : {}) })
  const [touched, setTouched] = useState<Set<keyof YieldBandDraft>>(new Set())
  const save = useLoadYieldBand({ inline: true })
  const machines = useMachines()
  const varieties = useVarieties(d.cropId || null)
  // The newest version with this key decides the version number, the note and the earliest date.
  const existing = useYieldBands({ cropId: d.cropId, stepCode: d.stepCode, latest: true, page: 1, limit: 200 }, Boolean(d.cropId && d.stepCode))
  const prev = previousBand(existing.data?.rows ?? [], d)
  const today = istDateKey()
  const p = validateYieldBandDraft(d, prev, today)
  const touch = (k: keyof YieldBandDraft) => setTouched((s) => new Set(s).add(k))
  const shown = (k: keyof YieldBandDraft) => (touched.has(k) ? p[k] : undefined)
  const set = (patch: Partial<YieldBandDraft>, k: keyof YieldBandDraft) => {
    setD((x) => ({ ...x, ...patch }))
    touch(k)
  }
  const err = save.isError ? serverProblem(save.error) : null
  const stepMachines = (machines.data ?? []).filter((m) => !d.stepCode || m.stepCodes.includes(d.stepCode))
  const nextVersion = (prev?.version ?? 0) + 1
  const keyFixed = Boolean(from)

  return (
    <FormDialog
      open
      onClose={onClose}
      title={from ? `New version · ${from.cropName ?? 'crop'}, ${from.stepCode}` : 'New yield band'}
      description={
        prev
          ? `Saves version ${nextVersion}; it replaces version ${prev.version} from its date. Runs already closed keep the band they were judged on.`
          : 'Saves version 1 for this crop, step and machine.'
      }
      saveLabel={`Save version ${nextVersion}`}
      loading={save.isPending}
      canSave={yieldBandOk(p) && !existing.isFetching}
      onSave={() => save.mutateAsync(yieldBandBody(d))}
    >
      {err && (
        <Banner tone="error" title={err.message}>
          {err.details.join(' · ')}
        </Banner>
      )}
      {keyFixed ? (
        <p className="text-[14px] text-muted">
          {from?.varietyName ?? 'Every variety'} · {from?.equipmentCode ?? 'all machines'}. Crop, variety, step and machine stay
          the same; for another machine, add a new band.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Crop" required error={shown('cropId')}>
            <Select
              value={d.cropId}
              onChange={(cropId) => set({ cropId, cropVarietyId: '' }, 'cropId')}
              placeholder="Choose…"
              options={crops.map((c) => ({ value: c.id, label: c.name }))}
              data-autofocus
            />
          </Field>
          <Field label="Variety" hint="Empty: every variety of the crop">
            <Select
              value={d.cropVarietyId}
              onChange={(cropVarietyId) => set({ cropVarietyId }, 'cropVarietyId')}
              options={[
                { value: '', label: varieties.isLoading ? 'Loading…' : 'Every variety' },
                ...(varieties.data ?? []).map((v) => ({ value: v.id, label: v.name })),
              ]}
              disabled={!d.cropId}
            />
          </Field>
          <Field label="Step" required error={shown('stepCode')}>
            <Select
              value={d.stepCode}
              onChange={(stepCode) => set({ stepCode }, 'stepCode')}
              placeholder="Choose…"
              options={steps.map((s) => ({ value: s.code, label: s.label }))}
            />
          </Field>
          <Field
            label="Machine"
            hint={
              d.stepCode && stepMachines.length === 0
                ? 'No machine does this step yet — set its steps on the Equipment page.'
                : 'Empty: all machines on this step'
            }
          >
            <Select
              value={d.equipmentId}
              onChange={(equipmentId) => set({ equipmentId }, 'equipmentId')}
              options={[{ value: '', label: machines.isLoading ? 'Loading…' : 'All machines' }, ...stepMachines.map((m) => ({ value: m.id, label: `${m.code} · ${m.name}` }))]}
            />
          </Field>
        </div>
      )}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Lowest yield" required error={shown('minPct')}>
          <Input
            inputMode="decimal"
            value={d.minPct}
            onChange={(e) => setD({ ...d, minPct: e.target.value })}
            onBlur={() => touch('minPct')}
            trailing="%"
            placeholder="68"
            className="font-mono"
            data-autofocus={keyFixed ? true : undefined}
          />
        </Field>
        <Field label="Highest yield" required error={shown('maxPct')}>
          <Input
            inputMode="decimal"
            value={d.maxPct}
            onChange={(e) => setD({ ...d, maxPct: e.target.value })}
            onBlur={() => touch('maxPct')}
            trailing="%"
            placeholder="74"
            className="font-mono"
          />
        </Field>
        <Field label="Min flush" error={shown('minFlushKg')} hint="Kilos the machine holds back; a flush below this is flagged">
          <Input
            inputMode="decimal"
            value={d.minFlushKg}
            onChange={(e) => setD({ ...d, minFlushKg: e.target.value })}
            onBlur={() => touch('minFlushKg')}
            trailing="kg"
            className="font-mono"
          />
        </Field>
        <Field label="Min input" error={shown('minInputKg')} hint="Smallest run the band holds for">
          <Input
            inputMode="decimal"
            value={d.minInputKg}
            onChange={(e) => setD({ ...d, minInputKg: e.target.value })}
            onBlur={() => touch('minInputKg')}
            trailing="kg"
            className="font-mono"
          />
        </Field>
        <Field label="In force from" error={shown('effectiveFrom')} hint={prev ? `Empty: today. Not before ${prev.effectiveFrom}.` : 'Empty: today'}>
          <Input type="date" value={d.effectiveFrom} min={prev?.effectiveFrom} onChange={(e) => set({ effectiveFrom: e.target.value }, 'effectiveFrom')} />
        </Field>
      </div>
      <Field label="Why it changes" required={Boolean(prev)} error={shown('changeNote')} hint={prev ? 'e.g. trial runs 12–14 Oct on the new sieve' : 'Optional on the first version'}>
        <Input value={d.changeNote} onChange={(e) => setD({ ...d, changeNote: e.target.value })} onBlur={() => touch('changeNote')} maxLength={200} />
      </Field>
    </FormDialog>
  )
}
